import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { cn } from "@/lib/utils";
import { Menu, X, Phone } from "lucide-react";

const navItems = [
  { to: "/", key: "home", end: true },
  { to: "/about", key: "about" },
  { to: "/menu", key: "menu" },
  { to: "/wine-list", key: "wine" },
  { to: "/gallery", key: "gallery" },
  { to: "/events", key: "events" },
  { to: "/reviews", key: "reviews" },
  { to: "/contact", key: "contact" },
];

export const Header = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={cn(
        "fixed top-0 inset-x-0 z-50 transition-all duration-500",
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-border/60 shadow-[0_1px_0_rgba(0,0,0,0.02)]"
          : "bg-white/55 backdrop-blur-md border-b border-white/40"
      )}
    >
      <div className="container-roze flex items-center justify-between h-20 md:h-24 gap-6">
        <Link
          to="/"
          data-testid="brand-logo"
          className="flex items-center gap-3 text-foreground shrink-0"
        >
          <span className="font-serif text-2xl md:text-3xl tracking-tight leading-none">
            Roze
          </span>
          <span className="hidden 2xl:inline-block text-[10px] uppercase tracking-[0.28em] pl-3 border-l border-border text-foreground/55 whitespace-nowrap">
            Restorāns · Liepāja
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {navItems.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              end={item.end}
              data-testid={`nav-link-${item.key}`}
              className={({ isActive }) =>
                cn(
                  "text-[11px] uppercase tracking-[0.22em] font-sans transition-colors relative pb-1 whitespace-nowrap",
                  "text-foreground/65 hover:text-foreground",
                  isActive && "text-foreground after:scale-x-100",
                  "after:absolute after:left-0 after:right-0 after:bottom-0 after:h-px after:scale-x-0 after:origin-left after:transition-transform after:bg-foreground"
                )
              }
            >
              {t(`nav.${item.key}`)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          <a
            href="tel:+37126111722"
            data-testid="header-phone-link"
            className="hidden xl:inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-foreground/65 hover:text-foreground transition-colors whitespace-nowrap"
          >
            <Phone className="h-3.5 w-3.5" strokeWidth={1.5} />
            +371 26 111 722
          </a>

          <LanguageSwitcher variant="default" />

          <Link
            to="/reserve"
            data-testid="header-reserve-cta"
            className="hidden md:inline-flex items-center px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] bg-primary text-primary-foreground hover:bg-primary/90 transition-colors whitespace-nowrap"
          >
            {t("nav.reserve")}
          </Link>

          <button
            type="button"
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden p-2 -mr-2 text-foreground"
            aria-label="Toggle navigation menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-border animate-fade-in"
        >
          <nav className="container-roze py-6 flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.key}
                to={item.to}
                end={item.end}
                data-testid={`mobile-nav-link-${item.key}`}
                className={({ isActive }) =>
                  cn(
                    "py-3 text-sm uppercase tracking-[0.22em] border-b border-border/60 transition-colors",
                    isActive ? "text-foreground" : "text-foreground/60 hover:text-foreground"
                  )
                }
              >
                {t(`nav.${item.key}`)}
              </NavLink>
            ))}
            <Link
              to="/reserve"
              data-testid="mobile-reserve-cta"
              className="mt-4 inline-flex items-center justify-center px-5 py-3 text-[11px] uppercase tracking-[0.22em] bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {t("nav.reserve")}
            </Link>
            <a
              href="tel:+37126111722"
              data-testid="mobile-phone-link"
              className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 text-[11px] uppercase tracking-[0.22em] border border-border text-foreground/80 hover:bg-muted"
            >
              <Phone className="h-3.5 w-3.5" />
              +371 26 111 722
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};
